import {
  lazy, // [MENU DEV]
  Suspense, // [MENU DEV]
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ComponentType,
  type JSX,
} from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import {
  cn,
  PendingActionProvider,
  ScreenNavProvider,
  ScreenUrlProvider,
  ThemeProvider,
  useOverlayMotion,
} from '@taki/ui';
import type { PendingAppAction, ScreenNav, ScreenSearch, ScreenUrlPort } from '@taki/ui';
import {
  createBrowserHistory,
  createRootRoute,
  createRoute,
  createRouter,
  Outlet,
  RouterProvider,
  useNavigate,
  useRouter,
  useRouterState,
} from '@tanstack/react-router';
import { AuthGuard } from './AuthGuard.js';
import { useDisplayScale } from './uiScale.js';
import { buildAppEntries } from './appRegistry.js';
import DeprecatedBand from './DeprecatedBand.js';
import { Dock } from './Dock.js';
import { HomePanel } from './HomePanel.js';
import type { HomeSection } from './HomePanel.js';
import { SettingsHubScreen } from './screens/SettingsHubScreen.js';
import { Notch } from './surfaces/Notch.js';
import { AskTakiAI } from './surfaces/AskTakiAI.js';
import { TakiProgressBar } from './surfaces/TakiProgressBar.js';
import type { ShellAppEntry, ShellAppRegistry } from './types.js';
// [MENU DEV] Bloc temporaire, suppression : packages/shell/src/dev/README.md
import { useVisibleAppEntries } from './dev/appVisibility.js';

// [MENU DEV] La ligne de version n'existe qu'en dev local. Meme couple que le
// menu DEV dans HomePanel.tsx : drapeau LITTERAL import.meta.env.DEV + import()
// dynamique. Au build de production la branche disparait, et avec elle le chunk
// de la bande. Ne JAMAIS remplacer import.meta.env.DEV par un helper importe,
// une variable intermediaire ou un optional chaining : c'est cette forme
// textuelle exacte que Vite remplace par une constante, et c'est ce
// remplacement qui permet a esbuild de supprimer la branche.
// A NE PAS CONFONDRE avec le TAMPON de build (/taki-build.json), qui est
// permanent et existe en production : voir tools/build-stamp/README.md.
const VersionBand = import.meta.env.DEV ? lazy(() => import('./dev/VersionBand.js')) : null;

// Le lanceur rapide (E5) n'est plus instancie : le dock + le menu home
// overlay le remplacent. Le composant reste au chaud dans
// surfaces/QuickLauncher.tsx, reactivable si le besoin revient.

export interface TakiShellProps {
  /** Config runtime chargee via `@taki/runtime-config`. */
  config: TakiConfig;
  /**
   * Registre optionnel surchargeant les apps placeholder par defaut. Indexe
   * par id d'app ; toute app installee sans surcharge recoit un ecran
   * placeholder genere.
   */
  apps?: ShellAppRegistry;
}

/** Libelle editorial des sections du panneau Home (titre d'onglet). */
const HOME_SECTION_LABELS: Record<HomeSection, string> = {
  apps: 'Apps',
  account: 'Mon compte',
  store: 'App Store',
};

/** Id d'app extrait d'un pathname /app/<id>, sinon undefined. */
function appIdOf(pathname: string): string | undefined {
  return pathname.startsWith('/app/') ? pathname.split('/')[2] : undefined;
}

/**
 * LES PARAMETRES D'ADRESSE D'UNE APP, et rien d'autre.
 *
 * `view` la vue active, `id` l'element ouvert, `s` la section (l'onglet de la
 * zone 1), `f` la pastille de filtre, `arch` le mode Archives. QUE DES
 * IDENTIFIANTS : jamais un nom, jamais un email, jamais le texte tape dans la
 * recherche (voir `screenUrl.tsx` dans @taki/ui). Le contenu de ces cinq
 * champs appartient a l'ecran, pas au shell : `useScreen` les lit et les ecrit
 * tout seul.
 *
 * `arch` N'EST PAS TYPE `string` COMME LES QUATRE AUTRES, et ce n'est pas une
 * negligence : voir `archRouterValue` juste dessous. Ne le "simplifie" pas.
 */
export interface AppSearch {
  view?: string;
  id?: string;
  s?: string;
  f?: string;
  arch?: string | number | boolean;
}

/**
 * LE PARAMETRE `arch`, DANS LA FORME QUE LE ROUTEUR SAIT REIMPRIMER TELLE
 * QUELLE. C'est le seul de nos parametres qui ait besoin de ca, et voici
 * pourquoi, parce que c'est invisible et que ca se "simplifie" tout seul a la
 * relecture suivante.
 *
 * TanStack Router (aucun `parseSearch` ni `stringifySearch` installes ici,
 * donc ses defauts) fait passer chaque valeur d'adresse par du JSON, dans les
 * DEUX sens : a la lecture il rend `?arch=1` en NOMBRE `1`, et a l'ecriture il
 * re-serialise la chaine `'1'` en `arch=%221%22` (une chaine JSON-parsable est
 * re-stringifiee, guillemets compris). Aucun de nos quatre autres parametres
 * ne declenche ca : ce sont des ids (`core.liste`, `tous`, `actifs`), et rien
 * de tout ca n'est du JSON valide, donc ils traversent en chaines intactes.
 * `arch=1` est la premiere valeur d'adresse du produit qui soit du JSON
 * valide. Consequences si on le traite comme les quatre autres : le mode ne
 * peut ni entrer dans l'adresse (elle affiche `arch=%221%22`) ni en etre relu
 * (le nombre `1` n'est pas une chaine, il tombe), sans la moindre erreur.
 *
 * La reponse est de DEFAIRE la conversion du routeur, sans jamais connaitre la
 * valeur qui allume le mode : ce fait-la vit a UN seul endroit, `ARCHIVES_ON`
 * dans `screenUrl.tsx`, et le recopier ici serait un fait ecrit deux fois. On
 * rend donc au routeur la forme JSON de son texte quand il y en a une (`'1'`
 * devient le nombre `1`, qu'il reimprime `arch=1`), et le texte tel quel
 * sinon. La symetrie est exacte, dans les deux sens, quelle que soit la
 * valeur.
 */
function archRouterValue(value: unknown): string | number | boolean | undefined {
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value !== 'string' || value === '') return undefined;
  try {
    const parsed: unknown = JSON.parse(value);
    // Les scalaires seulement : un objet ou un tableau n'a aucun sens dans nos
    // parametres, et il ne se relirait pas en texte.
    if (typeof parsed === 'number' || typeof parsed === 'boolean') return parsed;
  } catch {
    /* Pas du JSON : c'est du texte ordinaire, il traverse tel quel. */
  }
  return value;
}

/**
 * Validation des parametres d'adresse. Une adresse est du texte venu de
 * l'exterieur (un lien colle, un favori vieux de six mois, une faute de
 * frappe) : on ne garde que des chaines non vides, et une valeur absurde
 * disparait au lieu de traverser jusqu'a l'ecran.
 */
function validateAppSearch(search: Record<string, unknown>): AppSearch {
  const text = (key: string): string | undefined => {
    const value = search[key];
    return typeof value === 'string' && value !== '' ? value : undefined;
  };
  const validated: AppSearch = {};
  const view = text('view');
  const id = text('id');
  const section = text('s');
  const filter = text('f');
  const archives = archRouterValue(search['arch']);
  // Les cles absentes ne sont pas posees a `undefined` : le routeur ecrirait
  // sinon `?view=&id=` dans la barre d'adresse.
  if (view != null) validated.view = view;
  if (id != null) validated.id = id;
  if (section != null) validated.s = section;
  if (filter != null) validated.f = filter;
  if (archives != null) validated.arch = archives;
  return validated;
}

/**
 * L'adresse telle que l'ECRAN la lit : rien que des chaines
 * (`pickScreenSearch` de @taki/ui ne garde que ca, et `readArchivesParam` y
 * attend le TEXTE du parametre, pas la forme JSON du routeur). C'est la
 * seconde moitie de `archRouterValue` : on rend a `arch` le texte qu'il avait
 * dans la barre d'adresse, et c'est l'ecran, seul, qui decide de ce qu'il
 * signifie.
 */
function screenParamsOf(search: AppSearch): ScreenSearch {
  const params: ScreenSearch = {};
  if (search.view != null) params['view'] = search.view;
  if (search.id != null) params['id'] = search.id;
  if (search.s != null) params['s'] = search.s;
  if (search.f != null) params['f'] = search.f;
  if (search.arch != null) params['arch'] = String(search.arch);
  return params;
}

/**
 * Construit l'arbre de routes pour une config + des entrees d'apps resolues.
 *
 * Layout :
 *   __root__  →  ThemeProvider + AuthGuard + (surface de travail | bureau) + overlays
 *     /             →  bureau : panneau Home (section = etat interne du panneau)
 *     /account      →  bureau, section "Mon compte" preselectionnee
 *     /store        →  bureau, section "App Store" preselectionnee
 *     /settings     →  hub Parametres (meme layout que les apps : dock + plein ecran)
 *     /app/$appId   →  app installee montee paresseusement
 */
function buildRouter(config: TakiConfig, appEntries: ShellAppEntry[]) {
  const entriesById = new Map(appEntries.map((entry) => [entry.id, entry]));

  // Composants d'app RESOLUS (id -> export default du module). Les modules
  // sont aujourd'hui importes statiquement par le registre (bundle unique) :
  // on les resout tous au montage du shell et la navigation rend directement
  // le composant. Le montage "lazy" du routeur suspendait la route a CHAQUE
  // changement d'app et affichait une surface vide quelques frames : le
  // flash blanc constate par le fondateur.
  const resolvedApps = new Map<string, ComponentType>();

  const rootRoute = createRootRoute({
    component: function ShellLayout() {
      const navigate = useNavigate();
      const pathname = useRouterState({ select: (s) => s.location.pathname });

      // Le routeur ne type pas la recherche a la racine (chaque route a la
      // sienne) : on la prend en sac de cles brutes et c'est `validateAppSearch`
      // qui la ramene a nos quatre parametres.
      const search = useRouterState({
        select: (s) => s.location.search as Record<string, unknown>,
      });

      const [askAIOpen, setAskAIOpen] = useState(false);
      // Menu home : le panneau Home complet en overlay par-dessus la surface
      // de travail (comportement standard, ouvert par le logo Taki du dock).
      // Ouverture/fermeture animees via la norme du socle (useOverlayMotion) :
      // a la fermeture, le panneau reste monte le temps de l'animation de
      // sortie (homeMenu.closing) puis est demonte.
      const [homeMenuOpen, setHomeMenuOpen] = useState(false);
      const homeMenu = useOverlayMotion(homeMenuOpen);

      // Surface de travail PLEIN ECRAN (dock + contenu) : une app (/app/...)
      // ou le hub Parametres (/settings). Sinon on est sur le bureau (la
      // fenetre flottante Home).
      const inApp = pathname.startsWith('/app/') || pathname.startsWith('/settings');
      const appIdInPath = appIdOf(pathname);

      /*
       * LA MEMOIRE D'ADRESSE PAR APP, et sa raison n'est pas le confort.
       *
       * Sans elle, ouvrir une app se fait en DEUX temps VISIBLES : le routeur
       * pose d'abord `/app/<id>` tout nu (il navigue avant que l'ecran cible
       * n'existe), puis l'ecran se monte et ecrit ses parametres. La barre
       * d'adresse scintille, un etat intermediaire s'affiche une dizaine de
       * millisecondes. Aucun reglage d'effet ne rattrape ca, et ce n'est pas
       * faute d'avoir essaye : tant que la cible n'est pas connue AU CLIC, il y
       * a deux ecritures d'adresse.
       *
       * On retient donc l'adresse de chaque app visitee, et le dock y retourne
       * directement : l'ecran se monte sur une adresse qui dit deja tout, et il
       * n'a plus rien a reecrire. Une seule ecriture, aucun clignotement.
       *
       * Ce n'est PAS un changement de comportement : `useScreen` restaure deja
       * la vue et la fiche d'une app qu'on avait quittee (sessionStorage).
       * L'adresse se contente enfin de dire la meme chose que ce que l'ecran
       * allait montrer de toute facon.
       *
       * Memoire de PAGE (un F5 la vide, elle ne se partage pas) : elle ne
       * remplace pas la reprise de session, elle la suit. Premiere ouverture
       * d'une app dans la page : rien de memorise, donc l'ecran ecrit lui-meme,
       * comme avant. C'est la seule fois.
       */
      const lastSearchRef = useRef(new Map<string, AppSearch>());
      useEffect(() => {
        if (appIdInPath == null) return;
        lastSearchRef.current.set(appIdInPath, validateAppSearch(search));
      }, [appIdInPath, search]);
      const searchOf = (appId: string): AppSearch => lastSearchRef.current.get(appId) ?? {};

      // [MENU DEV] Le masquage vit dans les composants qui AFFICHENT, jamais
      // dans la cloture du routeur : entriesById et resolvedApps gardent la
      // liste complete, sinon masquer l'app ouverte la ferait tomber sur
      // "App introuvable".
      const visibleAppEntries = useVisibleAppEntries(appEntries);

      // Section du panneau Home preselectionnee par la route : la route "/"
      // porte tout (la section est un etat interne du panneau), /account et
      // /store restent fonctionnelles en preselectionnant.
      const homeSection: HomeSection = pathname.startsWith('/account')
        ? 'account'
        : pathname.startsWith('/store')
          ? 'store'
          : 'apps';

      // Titre de l'onglet : "Taki • <la ou on est>". Dans une app, son nom du
      // registre ; sur /settings, le hub ; ailleurs, la section du bureau. Le
      // titre statique de l'index.html ne sert que le temps du premier rendu.
      const currentLabel =
        (appIdInPath != null ? entriesById.get(appIdInPath)?.label : undefined) ??
        (pathname.startsWith('/settings') ? 'Parametres' : HOME_SECTION_LABELS[homeSection]);
      useEffect(() => {
        document.title = `Taki • ${currentLabel}`;
      }, [currentLabel]);

      // RETOUR : memorise la DERNIERE app visitee differente de la courante.
      // Quand on quitte /app/Y (vers une autre app, le hub ou le bureau), Y
      // devient la cible du bouton retour fourni aux ecrans via ScreenNav.
      const [lastAppId, setLastAppId] = useState<string | undefined>(undefined);
      const prevPathRef = useRef(pathname);
      useEffect(() => {
        const prev = prevPathRef.current;
        if (prev === pathname) return;
        const prevAppId = appIdOf(prev);
        if (prevAppId != null && prevAppId !== appIdInPath) setLastAppId(prevAppId);
        prevPathRef.current = pathname;
      }, [pathname, appIdInPath]);

      // L'app memorisee doit exister encore (registre) et ne pas etre l'app
      // courante ; sinon le retour renvoie au bureau ("Home"). [MENU DEV] elle
      // doit aussi etre VISIBLE : sans ca, le libelle "Retour a <app masquee>"
      // resterait affiche dans le chrome pendant une demo.
      const backEntry =
        lastAppId != null && lastAppId !== appIdInPath
          ? visibleAppEntries.find((entry) => entry.id === lastAppId)
          : undefined;
      const screenNav = useMemo<ScreenNav>(
        () => ({
          onBack: () => {
            if (backEntry != null) {
              // Meme adresse memorisee que le dock : le retour hors de l'app
              // ramene a l'app precedente telle qu'on l'avait laissee.
              void navigate({
                to: '/app/$appId',
                params: { appId: backEntry.id },
                search: searchOf(backEntry.id),
              });
            } else {
              void navigate({ to: '/' });
            }
          },
          backLabel: backEntry != null ? `Retour a ${backEntry.label}` : 'Home',
        }),
        [backEntry, navigate],
      );

      function pickApp(appId: string): void {
        setHomeMenuOpen(false);
        // DEJA DANS CETTE APP : on ne navigue pas. Une navigation vers la meme
        // route repartirait d'une adresse SANS parametres (le routeur ne
        // reconduit pas la recherche), donc l'ecran se verrait retirer sa
        // fiche ouverte, sa section et sa pastille, et une etape d'historique
        // serait brulee au passage. Le clic sur l'icone de l'app active, ou le
        // faux clic, ne doit rien casser. Meme garde que `pickShortcut`.
        if (appId === appIdInPath) return;
        // On repart de l'adresse memorisee : l'ecran cible se monte sur une
        // adresse complete, il n'a rien a reecrire, la barre ne scintille pas.
        const goToApp = (): Promise<void> =>
          navigate({ to: '/app/$appId', params: { appId }, search: searchOf(appId) });
        // Depuis le BUREAU (fenetre Home sur fond clair), l'arrivee dans
        // l'app se fait en crossfade natif. On appelle
        // document.startViewTransition NOUS-MEMES plutot que par l'option
        // viewTransition du routeur (drapeau interne consomme au mauvais
        // moment : aucun fondu visible) : le navigateur fige l'ecran courant,
        // la navigation s'execute dans le callback (DOM a jour a la
        // resolution de sa promesse, les stores du routeur committent en
        // synchrone), puis il fond entre les deux images (CSS
        // ::view-transition-* de tokens.css). Depuis une app, le changement
        // reste INSTANTANE (doctrine : pas de fondu d'entree sur la surface
        // de travail, le menu overlay porte deja son propre fondu de sortie).
        // Navigateur sans support : bascule directe, comme avant.
        if (!inApp && typeof document.startViewTransition === 'function') {
          document.startViewTransition(() => goToApp());
        } else {
          void goToApp();
        }
      }

      // Action de dock en attente : posee au clic d'un shortcut, consommee
      // par l'app visee (usePendingAppAction) qui ouvre son Modal puis
      // appelle consume() ; le shell la remet alors a null.
      const [pendingAction, setPendingAction] = useState<PendingAppAction | null>(null);
      const pendingActionValue = useMemo(
        () => ({ pending: pendingAction, consume: () => setPendingAction(null) }),
        [pendingAction],
      );

      function pickShortcut(appId: string, actionId: string): void {
        setPendingAction({ appId, actionId });
        // Deja dans l'app visee : poser l'action suffit, l'ecran monte la
        // consomme sans navigation.
        if (appId !== appIdInPath) {
          void navigate({ to: '/app/$appId', params: { appId }, search: searchOf(appId) });
        }
      }

      function openAskAI(): void {
        setHomeMenuOpen(false);
        setAskAIOpen(true);
      }

      useDisplayScale();

      // Pre-resolution de tous les ecrans d'app au boot : la navigation entre
      // apps est ensuite instantanee (aucune suspension, aucun flash blanc).
      useEffect(() => {
        for (const entry of appEntries) {
          if (resolvedApps.has(entry.id)) continue;
          void entry.load().then((mod) => {
            resolvedApps.set(entry.id, mod.default);
          });
        }
        // appEntries est une constante de cloture du routeur : une seule passe.
        // eslint-disable-next-line react-hooks/exhaustive-deps
      }, []);

      return (
        <ThemeProvider colors={config.branding.colors}>
          <AuthGuard config={config}>
            {/* Barre de progression de navigation : rendue une fois l'auth
                resolue (dans le sous-arbre protege), au-dessus de tout. Elle
                se pilote seule via l'etat du routeur. */}
            <TakiProgressBar />
            {/* CONTENEUR EN COLONNE. Il porte les bandes qui se posent au-dessus
                de la surface de travail. Il ne suffisait PAS d'ajouter un enfant :
                le <main> ci-dessous est une RANGEE flex, une bande s'y serait
                posee A COTE du dock. La colonne porte donc la hauteur pleine
                (h-screen), chaque bande prend sa rangee (shrink-0) et les deux
                branches ci-dessous se partagent le reste (min-h-0 flex-1).
                IL EST PERMANENT, et il ne l'a pas toujours ete : il est arrive
                avec la ligne de version, qui est un outil de DEV, mais la bande
                "app deprecated" ci-dessous existe en PRODUCTION et en depend.
                Retirer la greffe DEV ne retire donc PAS ce conteneur, ni les
                classes de hauteur qui vont avec (`min-h-0 flex-1` sur le <main>
                et sur le bureau).
                COUT CONNU de ce conteneur : il ne defile pas. Une bande allumee
                sur une fenetre basse (moins de ~34 rem de haut, environ 540 px)
                ecrete donc le haut et le bas de la fenetre Home, sans recours. Ca
                ne concerne que la ligne de version, qui est en dev et peut
                s'eteindre : la bande deprecated, elle, ne se rend QUE dans une
                app, jamais au-dessus du bureau, precisement pour ne pas exposer un
                client a cet ecretement. On ne corrige PAS en rabotant la hauteur
                du panneau Home : elle est validee par le fondateur. Le seuil est
                annonce dans dev/README.md section 3ter. */}
            <div className="flex h-screen w-full flex-col overflow-hidden">
              {/* [MENU DEV] Bande a retirer en entier, avec son Suspense. Le
                  fallback est `null` : une bande d'information n'a pas de raison
                  de faire clignoter un squelette en haut de l'ecran. */}
              {VersionBand != null && (
                <Suspense fallback={null}>
                  <VersionBand config={config} />
                </Suspense>
              )}
              {inApp ? (
                <>
                  {/* Bande PERMANENTE (elle existe en production, contrairement a
                      la ligne de version) : l'app ouverte est-elle deprecated ?
                      Montee ICI, dans la seule branche `inApp`, pour deux
                      raisons : hors d'une app il n'y a rien a signaler, et le
                      conteneur en colonne ne defile pas, donc une bande au-dessus
                      du bureau ecreterait la fenetre Home sur un ecran bas. */}
                  <DeprecatedBand appId={appIdInPath} />
                  {/* Surface de travail : PLEIN ECRAN, dock a gauche. Le conteneur
                   donne la hauteur et ne defile pas : c'est le gabarit
                   ScreenLayout (h-full, zone 5 scrollable) qui gere son propre
                   scroll. [MENU DEV] `min-h-0 flex-1` remplace `h-screen` : la
                   hauteur pleine est passee au conteneur en colonne ci-dessus. */}
                  <main className="flex min-h-0 w-full flex-1 overflow-hidden bg-surface">
                  <Dock
                    appEntries={visibleAppEntries} // [MENU DEV] remettre appEntries
                    activeAppId={appIdInPath}
                    onPickApp={pickApp}
                    onPickShortcut={pickShortcut}
                    onOpenHomeMenu={() => setHomeMenuOpen(true)}
                    onOpenSettings={() => void navigate({ to: '/settings' })}
                  />

                  <div className="min-w-0 flex-1 overflow-hidden">
                    {/* Le shell fournit onBack/backLabel aux ecrans (apps ET hub
                        /settings) : la puce d'icone du gabarit devient un bouton
                        retour vers la derniere app visitee, sinon le bureau. */}
                    <ScreenNavProvider value={screenNav}>
                      <PendingActionProvider value={pendingActionValue}>
                        {/* Changement d'app INSTANTANE : pas de fondu d'entree ni
                            de remontage force (cle) sur la surface de travail. Le
                            fondu global partait de opacity 0 a chaque navigation,
                            donc l'ecran repartait du transparent = un flash. Le
                            rail apparait direct ; seule la zone 5 fait un skeleton
                            si les donnees tardent vraiment (> 300ms). */}
                        <div className="h-full">
                          <Outlet />
                        </div>
                      </PendingActionProvider>
                    </ScreenNavProvider>
                  </div>
                  </main>
                </>
              ) : (
                /* Bureau. Centrage OPTIQUE : le panneau est remonte d'un cran
                   par rapport au centre geometrique (le vide sous une fenetre
                   flottante se lit plus leger que le vide au-dessus).
                   [MENU DEV] `min-h-0 flex-1` remplace `min-h-screen`, pour la
                   meme raison que le <main> ci-dessus : sans ce changement, la
                   bande plus une hauteur d'ecran pleine depassent le viewport. */
                <div className="flex min-h-0 w-full flex-1 items-center justify-center bg-bg p-4 pb-[5vh] sm:p-6 sm:pb-[5vh]">
                  <HomePanel
                    config={config}
                    appEntries={appEntries}
                    onPickApp={pickApp}
                    onAskAI={openAskAI}
                    initialSection={homeSection}
                  />
                </div>
              )}
            </div>

            {/* Notch DESACTIVE : le dock le remplace. Rendu conserve
                ci-dessous (fichier surfaces/Notch.tsx garde), reactivable en
                retirant `false &&`. */}
            {false && inApp && !askAIOpen && (
              <Notch expanded={homeMenuOpen} onToggle={() => setHomeMenuOpen((o) => !o)} />
            )}

            {/* MENU HOME (comportement standard) : le panneau Home complet en
                overlay par-dessus la surface de travail. Clic sur le voile =
                fermer et revenir a l'app ; changer de section reste DANS
                l'overlay (etat interne du panneau, aucune navigation) ;
                choisir une app = naviguer + fermer. */}
            {inApp && homeMenu.mounted && (
              <div
                className={cn(
                  'fixed inset-0 z-40 flex items-center justify-center p-4 pb-[5vh] sm:p-6 sm:pb-[5vh]',
                  // Pendant la sortie, l'overlay devient transparent aux
                  // clics : l'app redessous est utilisable sans attendre la
                  // fin du fondu.
                  homeMenu.closing && 'pointer-events-none',
                )}
              >
                {/* Voile bouton : un clic N'IMPORTE OU hors du panneau ferme
                    (curseur neutre, pas de selection de texte). */}
                <button
                  type="button"
                  aria-label="Fermer le menu"
                  onClick={() => setHomeMenuOpen(false)}
                  className={cn(
                    'absolute inset-0 cursor-default select-none bg-scrim backdrop-blur-sm',
                    homeMenu.closing ? 'taki-anim-fade-out' : 'taki-anim-fade',
                  )}
                />
                {/* pointer-events-none : le wrapper de centrage laisse passer
                    les clics des cotes vers le voile ; seul le panneau
                    lui-meme (pointer-events-auto) les intercepte. */}
                <div
                  role="dialog"
                  aria-label="Menu"
                  className="pointer-events-none relative z-10 flex w-full justify-center"
                >
                  <HomePanel
                    config={config}
                    appEntries={appEntries}
                    onPickApp={pickApp}
                    onAskAI={openAskAI}
                    className={cn(
                      homeMenu.closing ? 'taki-anim-pop-out' : 'taki-anim-pop pointer-events-auto',
                    )}
                  />
                </div>
              </div>
            )}

            <AskTakiAI open={askAIOpen} onClose={() => setAskAIOpen(false)} />
          </AuthGuard>
        </ThemeProvider>
      );
    },
  });

  // Le bureau (panneau Home) est rendu par le layout racine : la section est
  // un ETAT INTERNE du panneau, ces trois routes ne servent que d'ancres
  // d'URL (et de preselection de section).
  const indexRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/',
    component: () => null,
  });

  const accountRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/account',
    component: () => null,
  });

  const storeRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/store',
    component: () => null,
  });

  // Hub Parametres : route shell (pas /app/*), rendue dans le meme layout que
  // les apps (dock visible + surface pleine).
  const settingsRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/settings',
    component: function ShellSettings() {
      // [MENU DEV] Le hub recoit la liste COMPLETE, et filtre lui-meme ses
      // ONGLETS : son panneau Dock REECRIT les prefs dock.apps et
      // dock.shortcuts a partir de la liste qu'on lui donne. Filtrer ici
      // effacerait definitivement l'epinglage d'une app masquee, alors que le
      // masquage doit rester cosmetique et reversible.
      return <SettingsHubScreen appEntries={appEntries} />;
    },
  });

  // Une route paresseuse par app installee. `lazyRouteComponent` differe le
  // chargement du bundle de l'app jusqu'a la visite de sa route (code-splitting).
  const appRoute = createRoute({
    getParentRoute: () => rootRoute,
    path: '/app/$appId',
    validateSearch: validateAppSearch,
    component: function AppRouteComponent() {
      const { appId } = appRoute.useParams();
      const search = appRoute.useSearch();
      const navigate = useNavigate();
      const router = useRouter();
      const entry = entriesById.get(appId);

      /*
       * LE PORT D'URL passe a l'ecran (contrat : @taki/ui, screenUrl.tsx).
       * C'est le seul endroit du shell qui sait que l'etat d'un ecran a une
       * adresse ; l'ecran, lui, ne connait ni le routeur ni cette route.
       *
       * `push` n'est vrai que pour l'OUVERTURE d'une fiche : c'est la seule
       * navigation interne a un ecran dont le Retour du navigateur doit
       * s'occuper. Changer de vue, de section ou de pastille REMPLACE l'etape,
       * sinon trois clics dans le selecteur rendent le bouton Retour
       * inutilisable.
       */
      const url = useMemo<ScreenUrlPort>(
        () => ({
          // L'ecran ne voit que du TEXTE : c'est `screenParamsOf` qui defait
          // la forme JSON dans laquelle le routeur rend `arch`.
          params: screenParamsOf(search),
          setParams: (next, options) => {
            void navigate({
              to: '/app/$appId',
              params: { appId },
              search: validateAppSearch(next),
              replace: options?.push !== true,
              // Pas de fondu sur un changement d'etat INTERNE a l'ecran : le
              // crossfade du routeur est fait pour passer d'un ecran a un
              // autre, pas pour ouvrir une fiche.
              viewTransition: false,
            });
          },
          back: () => router.history.back(),
        }),
        [search, navigate, router, appId],
      );

      // Rendu DIRECT depuis le cache des composants resolus : aucune
      // suspension au changement d'app. Repli si un module n'est pas encore
      // resolu a la premiere visite (futur vrai code-splitting) : on le
      // charge puis on force un re-rendu ; en attendant la surface reste
      // vide et la barre de progression du shell couvre l'attente.
      const [, bump] = useReducer((n: number) => n + 1, 0);
      const AppComponent = entry != null ? resolvedApps.get(entry.id) : undefined;
      useEffect(() => {
        if (entry == null || resolvedApps.has(entry.id)) return;
        let alive = true;
        void entry.load().then((mod) => {
          resolvedApps.set(entry.id, mod.default);
          if (alive) bump();
        });
        return () => {
          alive = false;
        };
      }, [entry]);

      if (entry == null) {
        return (
          <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
            <h1 className="text-[1.375rem] font-semibold tracking-[-0.02em] text-ink-900">
              App introuvable
            </h1>
            <p className="text-sm text-ink-500">
              {`L'app "${appId}" n'est pas installee pour cette organisation.`}
            </p>
          </div>
        );
      }

      if (AppComponent == null) return null;
      return (
        // `key={appId}` : le fournisseur d'adresse est REMONTE a chaque
        // changement d'app. Il tient l'arbitre "une adresse, un ecran" ; sans
        // ce remontage, l'arbitre d'une app survivrait a la suivante, qui se
        // verrait refuser l'adresse par un ecran deja parti (l'adresse se
        // figeait alors sur /app/<id> jusqu'au rechargement de la page). La
        // route, elle, ne se demonte pas quand seul le parametre change : c'est
        // bien ce remontage-ci qu'il faut demander, pas celui du composant.
        <ScreenUrlProvider key={appId} value={url}>
          <AppComponent />
        </ScreenUrlProvider>
      );
    },
  });

  const routeTree = rootRoute.addChildren([
    indexRoute,
    accountRoute,
    storeRoute,
    settingsRoute,
    appRoute,
  ]);

  return createRouter({
    routeTree,
    // L'HISTORIQUE DU NAVIGATEUR, depuis le chantier socle 1 (30/07/2026).
    // Avant, le shell montait un historique MEMOIRE : rien n'etait visible dans
    // la barre d'adresse, F5 ramenait au bureau, aucun lien n'etait
    // partageable et le bouton Retour ne faisait rien. Le prix a payer est un
    // prerequis d'hebergement, deja tenu : le serveur doit renvoyer
    // `index.html` sur TOUTE route, sinon un lien partage tombe en 404 (voir
    // `rewrites` dans firebase.json).
    history: createBrowserHistory(),
    defaultPreload: 'intent',
    // Crossfade natif entre ecrans (View Transitions) : l'ANCIEN ecran reste
    // affiche pendant que le nouveau fond par-dessus (jamais de blanc, jamais
    // de depart du transparent). Duree et easing pilotes par les tokens dans
    // tokens.css (::view-transition-*). Navigateur sans support : changement
    // instantane, comme avant.
    defaultViewTransition: true,
  });
}

/**
 * Shell applicatif MyTaki.
 *
 * Lit `config.installedApps`, rend le bureau (panneau Home : rail de
 * navigation + contenu de section), monte une app par route (composants
 * pre-resolus au boot : navigation instantanee) derriere un dock d'apps, heberge le hub Parametres (/settings), theme le
 * tout depuis `config.branding`, et garde toute la surface derriere un auth
 * guard (ecran de connexion quand aucun utilisateur n'est connecte). Les
 * overlays systeme (menu home, Ask Taki AI) flottent au-dessus.
 */
export function TakiShell({ config, apps }: TakiShellProps): JSX.Element {
  const router = useMemo(() => {
    const entries = buildAppEntries(config.installedApps, apps);
    return buildRouter(config, entries);
  }, [config, apps]);

  return <RouterProvider router={router} />;
}
